import { Injectable, computed, inject, signal } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { getExhibitionSettings } from '@app/app.reducer';
import { Store, select } from '@ngrx/store';
import { RedeemPromoCode } from '@products/models/smart-promotions';
import { UserAccountTariffSpecificLimits } from '@products/models/tariff-status.model';
import { VoucherDetail, VoucherType, VoucherValidationState } from '@products/models/voucher.model';
import { VoucherWarningMessageService } from '@products/services/voucher-warning-message.service';
import { AppConstants } from '@shared/app-constants';
import { getSharedBookedPersonIdTariffsTotalCount } from '@store/products/booking/product/product-booking.selectors';
import { getBookedVoucherCountSortedByVoucherType } from '@store/products/booking/product/voucher/voucher-booking.selectors';
import { selectAllDiscountPromoCodes } from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import {
  getVoucherTypeState,
  isRedeemedAnonymousVoucherProductInProductSelectionList,
  isVoucherCodeRedeemed
} from '@store/products/product-selection/product-selection.selectors';
import { getAvailableTariffsByTicketPersonId } from '@store/products/status/tariff/tariff-status.selectors';
import { find, isEmpty, isNil, isNull, size, trim } from 'lodash';
import { Observable, combineLatest, first, iif, map, of, switchMap, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VoucherValidationService {
  private readonly store = inject(Store);
  private readonly voucherWarningMessageService = inject(VoucherWarningMessageService);

  #voucherValidationState = signal<VoucherValidationState>({
    maxTicketLimit: null,
    limitPromoCodes: null,
    limitLimitedPromoCodes: null,
    ticketLimitPerUserAccount: null,
    currentUserAccountTariffLimit: null,
    currentUserAccountTariffSpecificLimits: null,
    isVoucherIncludedPerUserAccountLimit: null,
    bookedProductsCount: null,
    isRedeemedAnonymousVoucherProductInProductSelectionList: null,
    isCurrentVoucherTypeMandatory: null,
    isCurrentVoucherTypeAnonymous: null
  });

  voucherValidationState = computed(this.#voucherValidationState);

  initState(voucherValidationState: VoucherValidationState) {
    this.checkValidityAndClearTicketLimitWarning();

    this.#voucherValidationState.set(voucherValidationState);
  }

  updateState(voucherValidationState: Partial<VoucherValidationState>) {
    this.checkValidityAndClearTicketLimitWarning();

    this.#voucherValidationState.update((voucherValidationServiceState) => ({
      ...voucherValidationServiceState,
      ...voucherValidationState
    }));
  }

  isValidTariffLimit(): boolean {
    const {
      ticketLimitPerUserAccount,
      isVoucherIncludedPerUserAccountLimit,
      currentUserAccountTariffLimit,
      bookedProductsCount,
      maxTicketLimit
    } = this.#voucherValidationState();

    if (!!ticketLimitPerUserAccount) {
      if (isVoucherIncludedPerUserAccountLimit) {
        if (
          !isNil(currentUserAccountTariffLimit) &&
          !isNil(bookedProductsCount) &&
          bookedProductsCount === currentUserAccountTariffLimit
        ) {
          this.voucherWarningMessageService.setTicketLimitPerUserAccountWarningMessage(ticketLimitPerUserAccount);

          return false;
        }
      }
    }

    if (!isNil(maxTicketLimit) && !isNil(bookedProductsCount) && bookedProductsCount === maxTicketLimit) {
      this.voucherWarningMessageService.setTicketLimitWarningMessage();

      return false;
    }

    return true;
  }

  isValidVoucherDetail$(voucherDetail: VoucherDetail): Observable<boolean> {
    const { ticketPersonId, voucherType, isValidVoucher } = voucherDetail;

    const { isVoucherIncludedPerUserAccountLimit, currentUserAccountTariffSpecificLimits } =
      this.voucherValidationState();

    return iif(
      () => isValidVoucher,
      this.store.select(getVoucherTypeState(ticketPersonId)).pipe(
        switchMap((voucherTypeState) => {
          if (isNull(voucherTypeState)) {
            this.voucherWarningMessageService.showInvalidVoucherWarningStatusMessage();

            return of(false);
          }

          const { initialNumberOfAvailableTariffs, isMandatory, isAnonymous, isAnonymousWithPrice } = voucherTypeState;

          return combineLatest([
            this.isUserAccountTariffSpecificLimitReached$(
              ticketPersonId,
              isVoucherIncludedPerUserAccountLimit,
              currentUserAccountTariffSpecificLimits
            ),
            this.isAvailableTariffsLimitReached$(ticketPersonId, initialNumberOfAvailableTariffs),
            this.isVoucherTypeLimitReached$(voucherType, isAnonymous),
            this.isAnonymousProductBookedAndCurrentVoucherMandatory$(isMandatory, isAnonymous, isAnonymousWithPrice)
          ]).pipe(
            first(),
            map((validatedVoucherDetails) =>
              validatedVoucherDetails.every((isInvalidVoucherDetail) => !isInvalidVoucherDetail)
            )
          );
        })
      ),
      of(false)
    );
  }

  validateRedeemedVoucherTypeCount$(
    redeemedVoucherType: VoucherType,
    redeemedVoucherCount: number,
    isRedeemedVoucherAnonymous: boolean
  ): Observable<number> {
    return combineLatest([
      this.store.select(getBookedVoucherCountSortedByVoucherType),
      this.store.select(getExhibitionSettings),
      this.store.select(isRedeemedAnonymousVoucherProductInProductSelectionList)
    ]).pipe(
      first(
        ([bookedVoucherCountSortedByVoucherType, exhibitionSettings]) =>
          !!bookedVoucherCountSortedByVoucherType && !!exhibitionSettings
      ),
      map(
        ([
          bookedVoucherCountSortedByVoucherType,
          exhibitionSettings,
          isRedeemedAnonymousVoucherProductInProductSelectionList
        ]) => {
          if (isRedeemedVoucherAnonymous && !isRedeemedAnonymousVoucherProductInProductSelectionList) {
            return redeemedVoucherCount;
          }

          const voucherTypeLimits = {
            [VoucherType.PromoCode]: exhibitionSettings.limitPromoCodes,
            [VoucherType.LimiterPromoCode]: exhibitionSettings.limitLimitedPromocodes,
            [VoucherType.OneTimeVoucher]: Infinity
          };

          const bookedAndRedeemedVoucherTypeCount =
            bookedVoucherCountSortedByVoucherType[redeemedVoucherType] + redeemedVoucherCount;

          const isVoucherTypeCountOverLimit =
            bookedAndRedeemedVoucherTypeCount > voucherTypeLimits[redeemedVoucherType];

          if (isVoucherTypeCountOverLimit) {
            const voucherTypeOverLimitCount =
              bookedAndRedeemedVoucherTypeCount - voucherTypeLimits[redeemedVoucherType];
            const voucherTypeValidatedCount = redeemedVoucherCount - voucherTypeOverLimitCount;

            return voucherTypeValidatedCount;
          }

          return redeemedVoucherCount;
        }
      )
    );
  }

  checkValidityAndClearVoucherWarning() {
    const { isRedeemedAnonymousVoucherProductInProductSelectionList, isCurrentVoucherTypeMandatory } =
      this.voucherValidationState();

    if (!isRedeemedAnonymousVoucherProductInProductSelectionList && !isCurrentVoucherTypeMandatory) {
      this.voucherWarningMessageService.clearVoucherWarningMessage();
    }
  }

  checkValidityAndClearTicketLimitWarning() {
    const { bookedProductsCount, maxTicketLimit } = this.voucherValidationState();

    if (bookedProductsCount < maxTicketLimit) {
      this.voucherWarningMessageService.clearTicketLimitWarningMessage();
    }
  }

  static emptyInputWithoutWhitespaceValidator() {
    return (control: AbstractControl<string>) =>
      size(control.value) && isEmpty(trim(control.value)) ? { emptyInputWithoutWhitespace: true } : null;
  }

  static voucherCodeAlreadyRedeemedValidator$(store: Store) {
    return (control: AbstractControl<string>) =>
      store.select(isVoucherCodeRedeemed(control.value)).pipe(
        map((isVoucherCodeRedeemed) => (isVoucherCodeRedeemed ? { voucherCodeAlreadyRedeemed: true } : null)),
        take(1)
      );
  }

  static promoCodeAlreadyRedeemedOrAppliedValidator$(store: Store) {
    return (control: AbstractControl<string>) =>
      store.select(selectAllDiscountPromoCodes).pipe(
        map((redeemedPromoCodes: RedeemPromoCode[]) => {
          const redeemedPromoCode = find(redeemedPromoCodes, ({ promoCode }) => promoCode === control.value);

          return !isNil(redeemedPromoCode)
            ? {
                promoCodeAlreadyRedeemed: !redeemedPromoCode.isRedeemedAndApplied,
                promoCodeAlreadyApplied: redeemedPromoCode.isRedeemedAndApplied
              }
            : null;
        }),
        take(1)
      );
  }

  private isUserAccountTariffSpecificLimitReached$(
    ticketPersonId: number,
    isVoucherIncludedPerUserAccountLimit: boolean,
    currentUserAccountTariffSpecificLimits: UserAccountTariffSpecificLimits
  ): Observable<boolean> {
    return this.store.pipe(
      select(getSharedBookedPersonIdTariffsTotalCount([])),
      map((sharedBookedPersonIdTariffsTotalCount) => {
        if (!isVoucherIncludedPerUserAccountLimit || isNil(currentUserAccountTariffSpecificLimits[ticketPersonId])) {
          return {
            isUserAccountTariffSpecificLimitReached: false,
            ticketLimit: 0
          };
        }

        const { availableTickets, ticketLimit } = currentUserAccountTariffSpecificLimits[ticketPersonId];

        const numberOfCurrentAlreadyBookedTariffs = sharedBookedPersonIdTariffsTotalCount[ticketPersonId] || 0;
        const isUserAccountTariffSpecificLimitReached =
          numberOfCurrentAlreadyBookedTariffs + AppConstants.VOUCHER_PRODUCT_DEFAULT_AMOUNT > availableTickets;

        return {
          isUserAccountTariffSpecificLimitReached,
          ticketLimit
        };
      }),
      tap(({ isUserAccountTariffSpecificLimitReached, ticketLimit }) => {
        if (isUserAccountTariffSpecificLimitReached) {
          this.voucherWarningMessageService.setTicketLimitPerUserAccountWarningMessage(ticketLimit);
        }
      }),
      map(({ isUserAccountTariffSpecificLimitReached }) => isUserAccountTariffSpecificLimitReached)
    );
  }

  private isAvailableTariffsLimitReached$(
    ticketPersonId: number,
    initialNumberOfAvailableTariffs: number
  ): Observable<boolean> {
    return this.store.pipe(
      select(getAvailableTariffsByTicketPersonId(ticketPersonId)),
      this.voucherWarningMessageService.showInvalidVoucherAvailableTariffsWarningMessagePipe(
        initialNumberOfAvailableTariffs
      ),
      map(({ currentNumberOfAvailableTariffs }) => !currentNumberOfAvailableTariffs)
    );
  }

  private isVoucherTypeLimitReached$(voucherType: VoucherType, isAnonymous: boolean): Observable<boolean> {
    return this.validateRedeemedVoucherTypeCount$(
      voucherType,
      AppConstants.VOUCHER_PRODUCT_DEFAULT_AMOUNT,
      isAnonymous
    ).pipe(
      this.voucherWarningMessageService.showInvalidVoucherTypeWarningMessagePipe(
        voucherType,
        this.voucherValidationState()
      ),
      map(({ voucherTypeValidatedCount }) => !voucherTypeValidatedCount)
    );
  }

  private isAnonymousProductBookedAndCurrentVoucherMandatory$(
    isMandatory: boolean,
    isAnonymous: boolean,
    isAnonymousWithPrice: boolean
  ): Observable<boolean> {
    this.updateState({
      isCurrentVoucherTypeMandatory: isMandatory,
      isCurrentVoucherTypeAnonymous: isAnonymous
    });

    if (isAnonymousWithPrice) {
      this.updateState({
        isRedeemedAnonymousVoucherProductInProductSelectionList: isAnonymous
      });

      this.voucherWarningMessageService.setVoucherAnonymousPriceTicketWarningMessage();

      return of(true);
    }

    if (this.voucherValidationState().isRedeemedAnonymousVoucherProductInProductSelectionList && isMandatory) {
      this.voucherWarningMessageService.setRegularVoucherAfterAnonymousTicketWarningMessage();

      return of(true);
    }

    return of(false);
  }
}
